/**
 * Row Virtualization Module
 *
 * Provides all virtualization-related functionality for the grid:
 *
 * 1. **Position Cache** (index-based): Maps row index → {offset, height, measured}
 *    - Rebuilt when row count changes (expand/collapse, filter)
 *    - Used for scroll position → row index lookups (binary search)
 *
 * 2. **Height Cache** (identity-based): Maps row identity → height
 *    - Persists across expand/collapse
 *    - Uses rowId string keys when available, WeakMap otherwise
 *    - Synthetic rows use __rowCacheKey for stable identity
 *
 * 3. **Virtual Window**: Computes which rows to render based on scroll position
 *
 * 4. **Row Measurement**: Measures rendered row heights from DOM
 */

// #region Types

/**
 * Position entry for a single row in the position cache.
 * @category Plugin Development
 */
export interface RowPosition {
  /** Cumulative offset from top in pixels */
  offset: number;
  /** Row height in pixels */
  height: number;
  /** Whether this is a measured value (true) or estimate (false) */
  measured: boolean;
}

/**
 * Height cache that persists row heights across position cache rebuilds.
 * Uses dual storage: Map for string keys (rowId, __rowCacheKey) and WeakMap for object refs.
 * @category Plugin Development
 */
export interface HeightCache {
  /** Heights keyed by string (for synthetic rows with __rowCacheKey or rowId-keyed rows) */
  byKey: Map<string, number>;
  /** Heights keyed by object reference (for data rows without rowId) */
  byRef: WeakMap<object, number>;
}

/**
 * Configuration for the position cache.
 */
export interface PositionCacheConfig<T = unknown> {
  /** Function to get row ID (if configured) */
  rowId?: (row: T) => string | number;
}

// #endregion

// #region Height Cache Operations

/**
 * Create a new empty height cache.
 */
export function createHeightCache(): HeightCache {
  return {
    byKey: new Map(),
    byRef: new WeakMap(),
  };
}

/**
 * Get the cache key for a row.
 * Returns string for synthetic rows (__rowCacheKey) or rowId-keyed rows,
 * or the row object itself for WeakMap lookup.
 */
export function getRowCacheKey<T>(row: T, rowId?: (row: T) => string | number): string | T {
  if (!row || typeof row !== 'object') return row;

  // 1. Synthetic rows: plugins MUST add __rowCacheKey
  if ('__rowCacheKey' in row) {
    return (row as { __rowCacheKey: string }).__rowCacheKey;
  }

  // 2. rowId property directly on the row (common pattern)
  if ('rowId' in row && (row as { rowId: string | number }).rowId != null) {
    return `id:${(row as { rowId: string | number }).rowId}`;
  }

  // 3. User-provided rowId function (if configured)
  if (rowId) {
    return `id:${rowId(row)}`;
  }

  // 4. Object identity (for WeakMap)
  return row;
}

/**
 * Get cached height for a row.
 * @returns Cached height or undefined if not cached
 */
export function getCachedHeight<T>(
  cache: HeightCache,
  row: T,
  rowId?: (row: T) => string | number,
): number | undefined {
  const key = getRowCacheKey(row, rowId);

  if (typeof key === 'string') {
    return cache.byKey.get(key);
  }

  // Object key - use WeakMap
  if (key && typeof key === 'object') {
    return cache.byRef.get(key);
  }

  return undefined;
}

/**
 * Set cached height for a row.
 */
export function setCachedHeight<T>(
  cache: HeightCache,
  row: T,
  height: number,
  rowId?: (row: T) => string | number,
): void {
  const key = getRowCacheKey(row, rowId);

  if (typeof key === 'string') {
    cache.byKey.set(key, height);
  } else if (key && typeof key === 'object') {
    cache.byRef.set(key, height);
  }
}

// #endregion

// #region Position Cache Operations

/**
 * Rebuild position cache preserving known heights from height cache.
 * Called when row count changes (expand/collapse, filter, data change).
 *
 * @param rows - Array of row data
 * @param heightCache - Height cache with persisted measurements
 * @param estimatedHeight - Estimated height for unmeasured rows
 * @param config - Position cache configuration
 * @param getPluginHeight - Optional function to get height from plugins
 * @returns New position cache
 */
export function rebuildPositionCache<T>(
  rows: T[],
  heightCache: HeightCache,
  estimatedHeight: number,
  config: PositionCacheConfig<T>,
  getPluginHeight?: (row: T, index: number) => number | undefined,
): RowPosition[] {
  const cache: RowPosition[] = new Array(rows.length);
  let offset = 0;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];

    // Height resolution order:
    // 1. Plugin's getRowHeight() (for synthetic rows with known heights)
    let height = getPluginHeight?.(row, i);
    let measured = height !== undefined;

    // 2. Cached height from previous measurements
    if (height === undefined) {
      height = getCachedHeight(heightCache, row, config.rowId);
      measured = height !== undefined;
    }

    // 3. Fall back to estimate
    if (height === undefined) {
      height = estimatedHeight;
      measured = false;
    }

    cache[i] = { offset, height, measured };
    offset += height;
  }

  return cache;
}

/**
 * Update a single row's height in the position cache.
 * Recalculates offsets for all subsequent rows.
 *
 * @param cache - Position cache to update
 * @param index - Row index to update
 * @param newHeight - New measured height
 */
export function updateRowHeight(cache: RowPosition[], index: number, newHeight: number): void {
  if (index < 0 || index >= cache.length) return;

  const entry = cache[index];
  const heightDiff = newHeight - entry.height;

  if (heightDiff === 0) return;

  // Update this row
  entry.height = newHeight;
  entry.measured = true;

  // Recalculate offsets for all subsequent rows
  for (let i = index + 1; i < cache.length; i++) {
    cache[i].offset += heightDiff;
  }
}

/**
 * Get total content height from position cache.
 *
 * @param cache - Position cache
 * @returns Total height in pixels
 */
export function getTotalHeight(cache: RowPosition[]): number {
  if (cache.length === 0) return 0;
  const last = cache[cache.length - 1];
  return last.offset + last.height;
}

// #endregion

// #region Binary Search

/**
 * Find the row index at a given scroll offset using binary search.
 * Returns the index of the row that contains the given pixel offset.
 *
 * @param cache - Position cache
 * @param targetOffset - Scroll offset in pixels
 * @returns Row index at that offset, or -1 if cache is empty
 */
export function getRowIndexAtOffset(cache: RowPosition[], targetOffset: number): number {
  if (cache.length === 0) return -1;
  if (targetOffset <= 0) return 0;

  let low = 0;
  let high = cache.length - 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const entry = cache[mid];
    const entryEnd = entry.offset + entry.height;

    if (targetOffset < entry.offset) {
      high = mid - 1;
    } else if (targetOffset >= entryEnd) {
      low = mid + 1;
    } else {
      // targetOffset is within this row
      return mid;
    }
  }

  // Return the closest row (low will be just past the target)
  return Math.max(0, Math.min(low, cache.length - 1));
}

// #endregion

// #region Statistics

/**
 * Calculate the average measured height.
 * Used for estimating unmeasured rows.
 *
 * @param cache - Position cache
 * @param defaultHeight - Default height to use if no measurements
 * @returns Average measured height
 */
export function calculateAverageHeight(cache: RowPosition[], defaultHeight: number): number {
  let totalHeight = 0;
  let measuredCount = 0;

  for (const entry of cache) {
    if (entry.measured) {
      totalHeight += entry.height;
      measuredCount++;
    }
  }

  return measuredCount > 0 ? totalHeight / measuredCount : defaultHeight;
}

/**
 * Count how many rows have been measured.
 *
 * @param cache - Position cache
 * @returns Number of measured rows
 */
export function countMeasuredRows(cache: RowPosition[]): number {
  let count = 0;
  for (const entry of cache) {
    if (entry.measured) count++;
  }
  return count;
}

// #endregion
// #region Row Measurement

/**
 * Result of measuring rendered row heights.
 */
export interface RowMeasurementResult {
  /** Whether any heights changed */
  hasChanges: boolean;
  /** Updated measured row count */
  measuredCount: number;
  /** Updated average height */
  averageHeight: number;
}

/**
 * Context for measuring rendered rows.
 */
export interface RowMeasurementContext<T> {
  /** Position cache to update */
  positionCache: RowPosition[];
  /** Height cache for persistence */
  heightCache: HeightCache;
  /** Row data array */
  rows: T[];
  /** Default row height */
  defaultHeight: number;
  /** Start index of rendered window */
  start: number;
  /** End index of rendered window (exclusive) */
  end: number;
  /** Function to get plugin height for a row */
  getPluginHeight?: (row: T, index: number) => number | undefined;
  /** Function to get row ID for height cache keying */
  getRowId?: (row: T) => string | number;
}

/**
 * Measure rendered row heights from DOM elements and update caches.
 * Returns measurement statistics for updating virtualization state.
 *
 * @param context - Measurement context with all dependencies
 * @param rowElements - NodeList of rendered row elements
 * @returns Measurement result with flags and statistics
 */
export function measureRenderedRowHeights<T>(
  context: RowMeasurementContext<T>,
  rowElements: NodeListOf<Element>,
): RowMeasurementResult {
  const { positionCache, heightCache, rows, start, end, getPluginHeight, getRowId } = context;

  let hasChanges = false;

  rowElements.forEach((rowEl) => {
    const rowIndexStr = (rowEl as HTMLElement).dataset.rowIndex;
    if (!rowIndexStr) return;

    const rowIndex = parseInt(rowIndexStr, 10);
    if (rowIndex < start || rowIndex >= end || rowIndex >= rows.length) return;

    const row = rows[rowIndex];

    // Check if a plugin provides the height for this row
    const pluginHeight = getPluginHeight?.(row, rowIndex);

    if (pluginHeight !== undefined) {
      // Plugin provides height - use it for position cache
      const currentEntry = positionCache[rowIndex];
      if (!currentEntry.measured || Math.abs(currentEntry.height - pluginHeight) > 1) {
        updateRowHeight(positionCache, rowIndex, pluginHeight);
        hasChanges = true;
      }
      return; // Don't measure DOM for plugin-managed rows
    }

    // No plugin height - use DOM measurement
    const measuredHeight = (rowEl as HTMLElement).offsetHeight;

    if (measuredHeight > 0) {
      const currentEntry = positionCache[rowIndex];

      // Only update if height differs significantly (> 1px to avoid oscillation)
      if (!currentEntry.measured || Math.abs(currentEntry.height - measuredHeight) > 1) {
        updateRowHeight(positionCache, rowIndex, measuredHeight);
        setCachedHeight(heightCache, row, measuredHeight, getRowId);
        hasChanges = true;
      }
    }
  });

  // Recompute stats
  const measuredCount = hasChanges ? countMeasuredRows(positionCache) : 0;
  const averageHeight = hasChanges ? calculateAverageHeight(positionCache, context.defaultHeight) : 0;

  return { hasChanges, measuredCount, averageHeight };
}

/**
 * Compute measurement statistics for a position cache, excluding plugin-managed rows.
 * Used after rebuilding position cache to get accurate averages for non-plugin rows.
 *
 * @param cache - Position cache
 * @param rows - Row data array
 * @param defaultHeight - Default height if no measurements
 * @param getPluginHeight - Function to check if plugin manages row height
 * @returns Object with measured count and average height
 */
export function computeAverageExcludingPluginRows<T>(
  cache: RowPosition[],
  rows: T[],
  defaultHeight: number,
  getPluginHeight?: (row: T, index: number) => number | undefined,
): { measuredCount: number; averageHeight: number } {
  let measuredCount = 0;
  let totalMeasured = 0;

  for (let i = 0; i < cache.length; i++) {
    const entry = cache[i];
    if (entry.measured) {
      // Only include in average if plugin doesn't provide height for this row
      const pluginHeight = getPluginHeight?.(rows[i], i);
      if (pluginHeight === undefined) {
        totalMeasured += entry.height;
        measuredCount++;
      }
    }
  }

  return {
    measuredCount,
    averageHeight: measuredCount > 0 ? totalMeasured / measuredCount : defaultHeight,
  };
}

// #endregion

// #region Scroll Mapping (max element-height cap)

/**
 * Maximum spacer element height (px) before browsers cap the rendered height.
 *
 * Chromium's hard cap is `2^25 = 33,554,432` px; Firefox/Safari are similar or larger.
 * We pick `33,500,000` — ~54 KB of headroom under Chromium's cap so that floating-point
 * drift in the fractional mapping math (`scrollTop * rMax / sMax`) cannot push a computed
 * spacer position past the engine cap and silently truncate.
 *
 * For datasets where `totalRows * rowHeight` exceeds this value, the faux-vscroll
 * spacer would silently truncate, making the tail of the dataset unreachable via
 * the native scrollbar / `Ctrl+End`. The grid switches to fractional scroll mapping
 * above this threshold — see {@link computeScrollMapping}.
 *
 * @category Plugin Development
 * @since 2.13.0
 */
export const MAX_ELEMENT_HEIGHT_PX = 33_500_000;

/**
 * Mapping between native `scrollTop` (clamped spacer space) and "virtual" scroll
 * position (raw row-content space). See {@link computeScrollMapping}.
 *
 * @category Plugin Development
 * @since 2.13.0
 */
export interface ScrollMapping {
  /** Raw row-content height in pixels (totalRows * rowHeight or sum of variable heights). */
  rawContentHeight: number;
  /** Effective spacer DOM height — `min(rawContentHeight, maxSpacerHeight)`. */
  spacerHeight: number;
  /** Viewport height used for computing scrollable extents. */
  viewportHeight: number;
  /** True when `rawContentHeight > maxSpacerHeight` — fractional mapping is active. */
  capped: boolean;
}

/**
 * Compute a mapping between the spacer's native scrollTop and the virtual
 * row-content scroll offset.
 *
 * For datasets within `maxSpacerHeight`, mapping is identity (no transform).
 * Above the cap, the spacer is clamped and `scrollTop` units no longer equal
 * row-content units — call {@link toVirtualScrollTop} / {@link fromVirtualScrollTop}
 * to translate between the two coordinate spaces.
 *
 * @category Plugin Development
 * @since 2.13.0
 */
export function computeScrollMapping(
  rawContentHeight: number,
  viewportHeight: number,
  maxSpacerHeight = MAX_ELEMENT_HEIGHT_PX,
): ScrollMapping {
  const spacerHeight = Math.min(rawContentHeight, maxSpacerHeight);
  return {
    rawContentHeight,
    spacerHeight,
    viewportHeight,
    capped: rawContentHeight > maxSpacerHeight,
  };
}

/**
 * Translate a native `scrollTop` (spacer space) into a virtual scroll offset
 * in raw row-content space. Identity when the mapping is not capped.
 *
 * The result is clamped to `[0, rawContentHeight - viewportHeight]` so callers
 * never index past the dataset, even when the spacer's actual scrollable extent
 * slightly exceeds `spacerHeight - viewportHeight` (e.g. when a horizontal scrollbar
 * adds bottom padding to the faux spacer).
 *
 * @category Plugin Development
 * @since 2.13.0
 */
export function toVirtualScrollTop(scrollTop: number, mapping: ScrollMapping): number {
  if (!mapping.capped) return scrollTop;
  const sMax = mapping.spacerHeight - mapping.viewportHeight;
  const rMax = mapping.rawContentHeight - mapping.viewportHeight;
  if (sMax <= 0 || rMax <= 0) return scrollTop;
  const virtual = (scrollTop * rMax) / sMax;
  if (virtual < 0) return 0;
  if (virtual > rMax) return rMax;
  return virtual;
}

/**
 * Translate a virtual scroll offset (raw row-content space) into a native
 * `scrollTop` value (spacer space). Identity when the mapping is not capped.
 *
 * @category Plugin Development
 * @since 2.13.0
 */
export function fromVirtualScrollTop(virtualTop: number, mapping: ScrollMapping): number {
  if (!mapping.capped) return virtualTop;
  const sMax = mapping.spacerHeight - mapping.viewportHeight;
  const rMax = mapping.rawContentHeight - mapping.viewportHeight;
  if (sMax <= 0 || rMax <= 0) return virtualTop;
  return (virtualTop * sMax) / rMax;
}

/**
 * Identity scroll mapping (no cap applied). Useful as a default in places that
 * don't yet know the dataset extents. `viewportHeight` is `0` until the first
 * `calculateTotalSpacerHeight` call populates the real extents — safe because
 * `capped: false` short-circuits both translation helpers before they read it.
 *
 * @category Plugin Development
 * @since 2.13.0
 */
export function createIdentityScrollMapping(): ScrollMapping {
  return { rawContentHeight: 0, spacerHeight: 0, viewportHeight: 0, capped: false };
}

// #endregion

// #region Fixed-Height Virtual Window

/**
 * Result of computing a virtual window for fixed-height rows.
 * Used by plugins like FilteringPlugin for virtualized dropdowns.
 */
export interface VirtualWindow {
  /** First row index to render (inclusive) */
  start: number;
  /** Last row index to render (exclusive) */
  end: number;
  /** Pixel offset to apply to the rows container (translateY) */
  offsetY: number;
  /**
   * Effective spacer DOM height — use this for the scrollable spacer element.
   * Equal to `min(totalRows * rowHeight, maxSpacerHeight)`. Above the cap this
   * is clamped; use {@link VirtualWindow.rawContentHeight} for the un-clamped value.
   */
  totalHeight: number;
  /**
   * Raw, un-clamped row-content height (`totalRows * rowHeight`). Equal to
   * `totalHeight` when the dataset is below `maxSpacerHeight`.
   *
   * @since 2.13.0
   */
  rawContentHeight: number;
}

/** Parameters for computing the virtual window */
export interface VirtualWindowParams {
  /** Total number of rows */
  totalRows: number;
  /** Height of the viewport in pixels */
  viewportHeight: number;
  /** Current scroll top position */
  scrollTop: number;
  /** Height of each row in pixels */
  rowHeight: number;
  /** Number of extra rows to render above/below viewport */
  overscan: number;
  /**
   * Optional cap on the spacer's DOM height. When `totalRows * rowHeight` exceeds
   * this value, `scrollTop` is treated as a spacer-space position and translated
   * to virtual row-content space via fractional mapping. Defaults to {@link MAX_ELEMENT_HEIGHT_PX}.
   * Pass `Infinity` to disable.
   *
   * @since 2.13.0
   */
  maxSpacerHeight?: number;
}

/**
 * Compute the virtual row window based on scroll position and viewport.
 * Simple fixed-height implementation for plugins needing basic virtualization.
 *
 * For datasets where `totalRows * rowHeight > maxSpacerHeight`, scroll position
 * is mapped fractionally so the entire dataset stays reachable past the browser's
 * single-element height cap (Chromium ~33.5M px). See {@link computeScrollMapping}.
 *
 * @param params - Parameters for computing the window
 * @returns VirtualWindow with start/end indices and transforms
 */
export function computeVirtualWindow(params: VirtualWindowParams): VirtualWindow {
  const { totalRows, viewportHeight, scrollTop, rowHeight, overscan, maxSpacerHeight = MAX_ELEMENT_HEIGHT_PX } = params;

  const visibleRows = Math.ceil(viewportHeight / rowHeight);
  const rawContentHeight = totalRows * rowHeight;
  const mapping = computeScrollMapping(rawContentHeight, viewportHeight, maxSpacerHeight);
  const virtualScrollTop = toVirtualScrollTop(scrollTop, mapping);

  // Render overscan rows in each direction (total window = visible + 2*overscan)
  let start = Math.floor(virtualScrollTop / rowHeight) - overscan;
  if (start < 0) start = 0;

  let end = start + visibleRows + overscan * 2;
  if (end > totalRows) end = totalRows;

  // Ensure start is adjusted if we hit the end
  if (end === totalRows && start > 0) {
    start = Math.max(0, end - visibleRows - overscan * 2);
  }

  return {
    start,
    end,
    offsetY: start * rowHeight,
    totalHeight: mapping.spacerHeight,
    rawContentHeight,
  };
}

/**
 * Determine if virtualization should be bypassed for small datasets.
 * When there are very few items, the overhead of virtualization isn't worth it.
 *
 * @param totalRows - Total number of items
 * @param threshold - Bypass threshold (skip virtualization if totalRows <= threshold)
 * @returns True if virtualization should be bypassed
 */
export function shouldBypassVirtualization(totalRows: number, threshold: number): boolean {
  return totalRows <= threshold;
}

// #endregion
